export type Params = typeof PARAMS

export const PARAMS = {
  companyId: ':companyId',
  projectId: ':projectId',
  brandId: ':brandId',
  brandTab: ':tab',
  brandElement: ':brandElement',
  categoryId: ':categoryId',
  editableCategory: ':editableCategory',
  fileMetaGroupId: ':fileMetaGroupId',
  sectionId: ':sectionId',
  blockId: ':blockId',
  nodeId: ':nodeId',
  taskId: ':taskId',
  fileId: ':fileId',
  previewSize: ':previewSize',
  activeTab: ':activeTabEnum',
  userId: ':userId',
  employeeId: ':employeeId',
  collectionId: ':collectionId',
  tagId: ':tagId',
  publicLinkId: ':publicLinkId',
  dashboardTag: ':dashboardTag',
  courseId: ':courseId',
  courseTab: ':courseTab',
  portalId: ':portalId',
  portalTab: ':portalTab',
  version: ':version',
  employeeTab: ':employeeTab',
  importId: ':importId',
  ssoId: ':ssoId',
  settingsTag: ':settingsTag',
  filePreviewTabs: ':filePreviewTabs',
  projectTranslationTabs: ':projectTranslationTabs',
  announcerId: ':announcerId',
  exportTab: ':exportTab',
  exportId: ':exportId',
  sessionId: ':sessionId',
} as const

export const PATHS = {
  app: '/app',
  auth: '/auth',
  error: '/error',
  public: '/public',
  saml: '/saml',
  dev: '/dev',
} as const

export const APP_PATHS = {
  home: `${PATHS.app}/h`,
  company: `${PATHS.app}/${PARAMS.companyId}`,
  admin: `${PATHS.app}/admin`,
} as const

export const SAML_PATHS = {
  home: `${PATHS.saml}`,
  providers: `${PATHS.saml}/providers`,
  users: `${PATHS.saml}/users`,
} as const

export const DEV_PATHS = {
  main: `${PATHS.dev}`,
  testData: `${PATHS.dev}/test-data`,
} as const

export const AUTH_PATHS = {
  login: `${PATHS.auth}/login`,
  logout: `${PATHS.auth}/logout`,
  signUp: `${PATHS.auth}/signup`,
  // policy: `${PATHS.auth}/privacy-policy`,
  // terms: `${PATHS.auth}/term-of-use`,
  // support: `${PATHS.auth}/support`,
  regSuccess: `${PATHS.auth}/registration-success`,
  regFailure: `${PATHS.auth}/registration-failure`,
  regConfirm: `${PATHS.auth}/registration-confirm`,
  restorePass: `${PATHS.auth}/restore-password`,
  newPass: `${PATHS.auth}/new-password`,
  createPass: `${PATHS.auth}/create-password`,
  welcomeBack: `${PATHS.auth}/welcome-back`,
  sso: `${PATHS.auth}/sso/${PARAMS.ssoId}?`,
} as const

export const ERROR_PATHS = {
  main: PATHS.error,
  notFound: `${PATHS.error}/404`,
  invalidLink: `${PATHS.error}/invalid-link`,
} as const

export const PUBLIC_PATHS = {
  main: `${PATHS.public}/${PARAMS.publicLinkId}/${PARAMS.sectionId}?/${PARAMS.blockId}?`,
} as const

export const HOME_PATHS = {
  home: `${APP_PATHS.home}`,
  companies: `${APP_PATHS.home}/companies`,
  user: `${APP_PATHS.home}/user`,
} as const

export const COMPANY_PATHS = {
  main: `${APP_PATHS.company}`,
  project: `${APP_PATHS.company}/project/${PARAMS.projectId}`,
  catalog: `${APP_PATHS.company}/projects/${PARAMS.categoryId}?`,
  transfer: `${APP_PATHS.company}/projects/${PARAMS.categoryId}/transfer`,
  files: `${APP_PATHS.company}/sources/${PARAMS.fileMetaGroupId}?`,
  employees: `${APP_PATHS.company}/employees`,
  trello: `${APP_PATHS.company}/trello`,
  dashboard: `${APP_PATHS.company}/dashboard/${PARAMS.dashboardTag}?`,
  tags: `${APP_PATHS.company}/tags`,
  brands: `${APP_PATHS.company}/brands`,
  fonts: `${APP_PATHS.company}/fonts`,
  settings: `${APP_PATHS.company}/settings/${PARAMS.settingsTag}?`,
  // profile: `${APP_PATHS.company}/profile`,
  collections: `${APP_PATHS.company}/collections`,
  analytics: `${APP_PATHS.company}/analytics`,
  contentSpace: `${APP_PATHS.company}/contentspace`,
  contentSpacePortals: `${APP_PATHS.company}/contentspace/portals`,
  contentSpaceCourses: `${APP_PATHS.company}/contentspace/courses`,
} as const

export const COMPANY_EMPLOYEES_PATHS = {
  main: `${COMPANY_PATHS.employees}`,
  employee: `${COMPANY_PATHS.employees}/employee/${PARAMS.employeeId}/${PARAMS.employeeTab}?`,
} as const

export const COMPANY_BRANDS_PATHS = {
  brands: `${COMPANY_PATHS.brands}`,
  brand: `${COMPANY_PATHS.brands}/brand/${PARAMS.brandId}/${PARAMS.brandTab}/${PARAMS.brandElement}?`,
  element: `${COMPANY_PATHS.brands}/brand/${PARAMS.brandId}/${PARAMS.brandTab}/${PARAMS.brandElement}`,
} as const

export const PROJECT_PATHS = {
  main: `${COMPANY_PATHS.project}`,
  home: `${COMPANY_PATHS.project}/home`,
  editor: `${COMPANY_PATHS.project}/editor/${PARAMS.sectionId}/block/${PARAMS.blockId}?`,
  preview: `${COMPANY_PATHS.project}/preview/${PARAMS.sectionId}?/block/${PARAMS.blockId}?`,
  previewPdf: `${COMPANY_PATHS.project}/pdf/${PARAMS.sectionId}?/block/${PARAMS.blockId}?`,
} as const

export const PROJECT_HOME_PATHS = {
  main: PROJECT_PATHS.home,
  info: `${PROJECT_PATHS.home}/info`,
  files: `${PROJECT_PATHS.home}/sources/${PARAMS.fileMetaGroupId}`,
  users: `${PROJECT_PATHS.home}/users`,
  export: `${PROJECT_PATHS.home}/export/${PARAMS.exportTab}?/${PARAMS.exportId}?`,
  cover: `${PROJECT_PATHS.home}/cover`,
  trello: `${PROJECT_PATHS.home}/trello`,
  sections: `${PROJECT_PATHS.home}/sections/${PARAMS.sectionId}?`,
  chat: `${PROJECT_PATHS.home}/chat`,
  translation: `${PROJECT_PATHS.home}/translation/${PARAMS.projectTranslationTabs}?`,
} as const

export const PROJECT_TRELLO_PATHS = {
  main: `${PROJECT_HOME_PATHS.trello}`,
  task: `${PROJECT_HOME_PATHS.trello}/${PARAMS.taskId}`,
} as const

export const COMPANY_FILES_PATHS = filesPath(COMPANY_PATHS.files)
export const PROJECT_FILES_PATHS = filesPath(PROJECT_HOME_PATHS.files)
export const COMPANY_TRELLO_PATHS = {
  main: `${COMPANY_PATHS.trello}`,
  task: `${COMPANY_PATHS.trello}/${PARAMS.taskId}`,
} as const

export const COMPANY_COLLECTION_PATHS = {
  collections: `${COMPANY_PATHS.collections}`,
  collection: `${COMPANY_PATHS.collections}/collection/${PARAMS.collectionId}`,
  preview: `${COMPANY_PATHS.collections}/preview/${PARAMS.collectionId}`,
} as const

export const COMPANY_CONTENT_SPACE_PATHS = {
  home: `${COMPANY_PATHS.contentSpace}`,
  portals: `${COMPANY_PATHS.contentSpacePortals}`,
  courses: `${COMPANY_PATHS.contentSpaceCourses}`,
  course: `${COMPANY_PATHS.contentSpaceCourses}/${PARAMS.courseId}/${PARAMS.courseTab}`,
  courseBase: `${COMPANY_PATHS.contentSpaceCourses}/${PARAMS.courseId}`,
  session: `${COMPANY_PATHS.contentSpaceCourses}/${PARAMS.courseId}/session/${PARAMS.sessionId}`,
  player: `${COMPANY_PATHS.contentSpace}/player/${PARAMS.courseId}`,
  portal: `${COMPANY_PATHS.contentSpacePortals}/${PARAMS.portalId}/${PARAMS.portalTab}`,
  portalBase: `${COMPANY_PATHS.contentSpacePortals}/${PARAMS.portalId}`,
} as const

// Legacy V1 LMS player — lives under /lms/* (V2 inline player owns the root).
// Logout is shared at root /logout (served by V2 shell), not duplicated under /lms.
export const PORTAL_MODE_PATHS = {
  root: '/lms',
  course: `/lms/courses/${PARAMS.courseId}`,
  courseSection: `/lms/courses/${PARAMS.courseId}/sections/${PARAMS.sectionId}`,
  logout: '/logout',
} as const

export const EDITOR_PATHS = {
  main: `${PROJECT_PATHS.editor}`,
} as const

export const SCORM_PATHS = {
  mainV2: `/static-scorm-v2/${PARAMS.projectId}/${PARAMS.version}/(section)?/${PARAMS.sectionId}?/(block)?/${PARAMS.blockId}?`,
  mainV1: `/static-scorm/${PARAMS.projectId}/(section)?/${PARAMS.sectionId}?/(block)?/${PARAMS.blockId}?`,
  fallback: `/static-scorm-fallback/${PARAMS.projectId}`,
} as const

export const PDF_PATHS = {
  main: `/static-pdf/${PARAMS.projectId}`,
} as const

// Portal V2 inline player — three canonical URL shapes, mirroring the backend
// routes in apps/backend/src/services-v2/contentspace/routes/portal-v2-spa.public.ts.
// path-to-regexp v1.x (react-router 5) can't express "optional deeper segment
// with literal prefix" in a single string, so we expose each shape separately
// and use the array in matchPath/<Route path> to reject non-canonical URLs.
// `:sectionId?` / `:blockId?` keep these params optional in StrictRouteParams so
// the shared ProjectUrlType (distributed union of required keys) doesn't make
// them required across every other player path. Matching stays strict because
// the literal `/sections/` / `/blocks/` prefixes are mandatory.
export const PORTAL_V2_PATHS = {
  course: `/courses/${PARAMS.courseId}`,
  courseSection: `/courses/${PARAMS.courseId}/sections/${PARAMS.sectionId}?`,
  courseBlock: `/courses/${PARAMS.courseId}/sections/${PARAMS.sectionId}?/blocks/${PARAMS.blockId}?`,
} as const
export const PORTAL_V2_LMS_PATHS = {
  lmsCourse: `/lms/courses/${PARAMS.courseId}`,
  lmsCourseSection: `/lms/courses/${PARAMS.courseId}/sections/${PARAMS.sectionId}?`,
  lmsCourseBlock: `/lms/courses/${PARAMS.courseId}/sections/${PARAMS.sectionId}?/blocks/${PARAMS.blockId}?`,
} as const

// Specific → general ordering so matchPath picks the deepest pattern first.
// lms-prefixed duplicates exist so PortalV2Player also owns the legacy V1 LMS
// URLs once the backend starts serving portal-bundle on /lms/*.
export const PORTAL_V2_COURSE_MATCH = [
  PORTAL_V2_PATHS.courseBlock,
  PORTAL_V2_PATHS.courseSection,
  PORTAL_V2_PATHS.course,
] as const

export const PORTAL_V2_LMS_COURSE_MATCH = [
  PORTAL_V2_LMS_PATHS.lmsCourseBlock,
  PORTAL_V2_LMS_PATHS.lmsCourseSection,
  PORTAL_V2_LMS_PATHS.lmsCourse,
] as const

export type PortalV2CoursePath = (typeof PORTAL_V2_COURSE_MATCH)[number]

export const isPortalV2CoursePath = (path: unknown): path is PortalV2CoursePath =>
  typeof path === 'string' && (PORTAL_V2_COURSE_MATCH as readonly string[]).includes(path)

export const buildPortalV2CourseUrl = (
  params: {
    courseId?: string
    sectionId?: string | null
    blockId?: string | null
  },
  options: { lms?: boolean } = {},
): string => {
  const { courseId, sectionId, blockId } = params
  if (!courseId) {
    return ''
  }
  const prefix = options.lms ? '/lms/courses' : '/courses'
  if (sectionId && blockId) {
    return `${prefix}/${courseId}/sections/${sectionId}/blocks/${blockId}`
  }
  if (sectionId) {
    return `${prefix}/${courseId}/sections/${sectionId}`
  }
  return `${prefix}/${courseId}`
}

export const ADMIN_PATHS = {
  main: `${APP_PATHS.admin}`,
  companies: `${APP_PATHS.admin}/companies`,
  company: `${APP_PATHS.admin}/company/${PARAMS.companyId}`,
  users: `${APP_PATHS.admin}/users`,
  announcers: `${APP_PATHS.admin}/announcers`,
} as const

export const ADMIN_COMPANIES = {
  main: ADMIN_PATHS.companies,
  company: `${ADMIN_PATHS.companies}/${PARAMS.companyId}`,
  users: `${ADMIN_PATHS.companies}/${PARAMS.companyId}/users`,
} as const

export const ADMIN_USERS = {
  main: ADMIN_PATHS.users,
  user: `${ADMIN_PATHS.users}/${PARAMS.userId}`,
} as const

export const ADMIN_ANNOUNCERS = {
  main: ADMIN_PATHS.announcers,
  announcer: `${ADMIN_PATHS.announcers}/${PARAMS.announcerId}`,
} as const

function filesPath<T extends string>(match: T) {
  return {
    main: `${match}`,
    preview: `${match}/preview/${PARAMS.fileId}/${PARAMS.filePreviewTabs}?`,
    // add: `${match}/add`,
    edit: `${match}/edit/${PARAMS.editableCategory}`,
    move: `${match}/move`,
    delete: `${match}/delete`,
  } as const
}
